import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NodeBase, NodeType, Domain, Purdue, ZoneType } from '@/types/assets';
import { useAssetsStore } from '@/stores/assetsStore';
import { useSiteStore } from '@/stores/siteStore';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, MapPin, Building2, Network, Server, Users, Info } from 'lucide-react';
import { useEffect } from 'react';
import { SiteMap } from '@/components/sites/SiteMap';

const nodeSchema = z.object({
  label: z.string().min(1, 'Name is required'),
  type: z.enum(['site', 'zone', 'system', 'endpoint', 'supplier', 'cloud']),
  zoneType: z.enum(['IT', 'OT', 'DMZ', 'Cloud']).optional(),
  domain: z.enum(['IT', 'OT', 'Cloud', 'DMZ', 'External', 'IoT', 'Physical']).optional(),
  purdue_level: z.enum(['L0', 'L1', 'L2', 'L3', 'L3.5', 'L4', 'L5']).optional(),
  category: z.string().optional(),
  parentId: z.string().optional(),
  siteId: z.string().optional(),
  location: z.string().optional(),
  description: z.string().optional(),
  ip: z.string().optional(),
  model: z.string().optional(),
  vendor: z.string().optional(),
  serial: z.string().optional(),
  fw_version: z.string().optional(),
  criticality: z.enum(['High', 'Medium', 'Low']).optional(),
  lifecycle_state: z.enum(['prod', 'test', 'dev', 'retired']).optional(),
  managedSystems: z.array(z.string()).optional(),
  // Site-specific fields
  site_code: z.string().max(8).optional(),
  address: z.string().optional(),
  country: z.string().optional(),
  timezone: z.string().optional(),
  site_criticality: z.enum(['Very Low', 'Low', 'Medium', 'High', 'Very High']).optional(),
  owner_id: z.string().optional(),
  physical_security: z.enum(['Basic', 'Controlled', 'Restricted']).optional(),
  operating_start_time: z.string().optional(),
  operating_end_time: z.string().optional(),
  operating_weekdays: z.array(z.string()).optional(),
  lat: z.number().optional(),
  lon: z.number().optional(),
});

type NodeFormData = z.infer<typeof nodeSchema>;

interface NodeCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position?: { x: number; y: number };
}

export function NodeCreateDialog({ open, onOpenChange, position }: NodeCreateDialogProps) {
  const { addNode, nodes, loadAssets } = useAssetsStore();
  const { sites, loadSites, orgUsers, loadOrgUsers } = useSiteStore();
  const [step, setStep] = useState(1);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<NodeFormData>({
    resolver: zodResolver(nodeSchema),
    defaultValues: {
      managedSystems: [],
      operating_start_time: '07:00',
      operating_end_time: '16:00',
      operating_weekdays: ['mon', 'tue', 'wed', 'thu', 'fri'],
    },
  });

  const watchedType = watch('type');
  const totalSteps = watchedType === 'site' ? 1 : 3;
  const [showMapPicker, setShowMapPicker] = useState(false);

  useEffect(() => {
    if (open) {
      loadSites();
      loadOrgUsers();
    }
  }, [open, loadSites, loadOrgUsers]);

  const watchedZoneType = watch('zoneType');
  const watchedDomain = watch('domain');
  const watchedLat = watch('lat');
  const watchedLon = watch('lon');

  const handleMapClick = (coordinates: { lat: number; lon: number }) => {
    setValue('lat', coordinates.lat);
    setValue('lon', coordinates.lon);
    setShowMapPicker(false);
  };

  const handleCancelCoordinates = () => {
    setShowMapPicker(false);
  };

  const onSubmit = async (data: NodeFormData) => {
    try {
      const id = Math.random().toString(36).substring(2, 15);
      
      const nodeData: NodeBase = {
        id,
        type: data.type,
        label: data.label,
        domain: data.domain || (data.zoneType as Domain),
        purdue_level: data.purdue_level,
        category: data.category,
        parentId: data.parentId,
        position: position || { x: Math.random() * 400, y: Math.random() * 400 },
        meta: {
          zoneType: data.zoneType,
          location: data.location,
          description: data.description,
          ip: data.ip,
          model: data.model,
          vendor: data.vendor,
          serial: data.serial,
          fw_version: data.fw_version,
          criticality: data.criticality,
          lifecycle_state: data.lifecycle_state,
          managedSystems: data.managedSystems,
          // Site-specific data
          site_code: data.site_code,
          address: data.address,
          country: data.country,
          timezone: data.timezone,
          site_criticality: data.site_criticality,
          owner_id: data.owner_id,
          physical_security: data.physical_security,
          operating_hours: data.operating_start_time && data.operating_end_time ? {
            start_time: data.operating_start_time,
            end_time: data.operating_end_time,
            weekdays: data.operating_weekdays || [],
          } : undefined,
          lat: data.lat,
          lon: data.lon,
        },
      };

      const node = {
        id,
        type: 'asset',
        position: position || { x: Math.random() * 400, y: Math.random() * 400 },
        data: nodeData,
      };

      await addNode(node);
      toast.success(`${data.type} "${data.label}" created successfully`);
      
      // Reload assets to refresh the view
      await loadAssets();
      
      reset();
      setStep(1);
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to create node');
      console.error('Error creating node:', error);
    }
  };

  const getParentNodes = () => {
    switch (watchedType) {
      case 'zone':
        return nodes.filter(n => n.data.type === 'site');
      case 'system':
        return nodes.filter(n => n.data.type === 'zone');
      case 'endpoint':
        return nodes.filter(n => n.data.type === 'system');
      default:
        return [];
    }
  };

  const getParentLabel = () => {
    switch (watchedType) {
      case 'zone': return 'Site';
      case 'system': return 'Zone';
      case 'endpoint': return 'System';
      default: return 'Parent';
    }
  };

  const getStepTitle = () => {
    if (watchedType === 'site') return 'Create Site';
    
    switch (step) {
      case 1: return 'Choose Node Type';
      case 2: return 'Basic Information';
      case 3: return 'Technical Details';
      default: return 'Create Node';
    }
  };

  const canProceedToNextStep = () => {
    switch (step) {
      case 1: return watchedType;
      case 2: {
        const label = watch('label');
        const parentId = watch('parentId');
        const zoneType = watch('zoneType');
        
        if (!label) return false;
        
        // For sites and suppliers - just need name
        if (watchedType === 'site' || watchedType === 'supplier') return true;
        
        // For zones - need name, zone type, and parent site
        if (watchedType === 'zone') return zoneType && parentId;
        
        // For other types - need parent
        return parentId;
      }
      case 3: return true;
      default: return false;
    }
  };

  const resetForm = () => {
    reset();
    setStep(1);
  };

  const NodeTypeIcon = ({ type }: { type: NodeType }) => {
    switch (type) {
      case 'site': return <MapPin className="h-5 w-5" />;
      case 'zone': return <Building2 className="h-5 w-5" />;
      case 'system': return <Network className="h-5 w-5" />;
      case 'endpoint': return <Server className="h-5 w-5" />;
      case 'supplier': return <Users className="h-5 w-5" />;
      default: return <Server className="h-5 w-5" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      onOpenChange(open);
      if (!open) resetForm();
    }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getStepTitle()}
            <Badge variant="outline">{step}/{totalSteps}</Badge>
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* For sites: Show all fields directly */}
          {(watchedType === 'site') && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="label">Name *</Label>
                  <Input
                    id="label"
                    {...register('label')}
                    placeholder="Enter asset name"
                  />
                  {errors.label && (
                    <p className="text-sm text-danger">{errors.label.message}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-1">
                    <Label htmlFor="site_code">Site Code</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Short identifier for the site (max 8 characters)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Input
                    id="site_code"
                    {...register('site_code')}
                    placeholder="e.g., HQ-01"
                    maxLength={8}
                  />
                </div>

                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    {...register('country')}
                    placeholder="Country code (e.g., SE, US)"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    {...register('address')}
                    placeholder="Street address, City"
                  />
                </div>

                <div>
                  <Label htmlFor="timezone">Timezone</Label>
                  <Input
                    id="timezone"
                    {...register('timezone')}
                    placeholder="Europe/Stockholm"
                  />
                </div>

                <div>
                  <div className="flex items-center gap-1">
                    <Label htmlFor="site_criticality">Site Criticality</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Overall importance of the site to operations</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select onValueChange={(value) => setValue('site_criticality', value as 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select criticality" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Very Low">Very Low</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Very High">Very High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* GPS Coordinates Section */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-medium text-sm mb-3">GPS Coordinates</h4>
                  <div className="space-y-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowMapPicker(true)}
                      className="w-full"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Pick from Map
                    </Button>
                    
                    {showMapPicker && (
                      <div className="border rounded-lg overflow-hidden" style={{ height: '400px' }}>
                        <SiteMap
                          sites={[]}
                          onMapClick={handleMapClick}
                          className="h-full"
                        />
                        <div className="p-3 bg-muted flex gap-2 justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleCancelCoordinates}
                          >
                            Close Map
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="lat">Latitude</Label>
                        <Input
                          id="lat"
                          type="number"
                          step="any"
                          value={watchedLat || ''}
                          onChange={(e) => setValue('lat', parseFloat(e.target.value) || undefined)}
                          placeholder="59.3293"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lon">Longitude</Label>
                        <Input
                          id="lon"
                          type="number"
                          step="any"
                          value={watchedLon || ''}
                          onChange={(e) => setValue('lon', parseFloat(e.target.value) || undefined)}
                          placeholder="18.0686"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Site Ownership Section */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-medium text-sm mb-3">Site Ownership</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <div className="flex items-center gap-1">
                        <Label htmlFor="owner_id">Primary Owner</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3 w-3 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Person responsible for this site</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Select onValueChange={(value) => setValue('owner_id', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select owner" />
                        </SelectTrigger>
                        <SelectContent>
                          {orgUsers.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              <div className="flex flex-col">
                                <span>{user.name}</span>
                                <span className="text-xs text-muted-foreground">{user.email}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Security & Operations Section */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-medium text-sm mb-3">Security & Operations</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="physical_security">Physical Security</Label>
                      <Select onValueChange={(value) => setValue('physical_security', value as 'Basic' | 'Controlled' | 'Restricted')}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select security level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Basic">Basic</SelectItem>
                          <SelectItem value="Controlled">Controlled</SelectItem>
                          <SelectItem value="Restricted">Restricted</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-2">
                      <div className="flex items-center justify-between mb-2">
                        <Label>Operation Hours</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const is247 = watch('operating_weekdays')?.length === 7 && 
                                          watch('operating_start_time') === '00:00' && 
                                          watch('operating_end_time') === '23:59';
                            if (is247) {
                              setValue('operating_start_time', '07:00');
                              setValue('operating_end_time', '16:00');
                              setValue('operating_weekdays', ['mon', 'tue', 'wed', 'thu', 'fri']);
                            } else {
                              setValue('operating_start_time', '00:00');
                              setValue('operating_end_time', '23:59');
                              setValue('operating_weekdays', ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
                            }
                          }}
                        >
                          {watch('operating_weekdays')?.length === 7 && 
                           watch('operating_start_time') === '00:00' && 
                           watch('operating_end_time') === '23:59' ? '✓ ' : ''}24/7 365 Operation
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="operating_start_time" className="text-sm">Start Time</Label>
                          <Input
                            id="operating_start_time"
                            type="time"
                            {...register('operating_start_time')}
                          />
                        </div>
                        <div>
                          <Label htmlFor="operating_end_time" className="text-sm">End Time</Label>
                          <Input
                            id="operating_end_time"
                            type="time"
                            {...register('operating_end_time')}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-span-2">
                      <Label>Weekdays</Label>
                      <div className="flex gap-2 mt-2">
                        {[
                          { code: 'mon', label: 'Mon' },
                          { code: 'tue', label: 'Tue' },
                          { code: 'wed', label: 'Wed' },
                          { code: 'thu', label: 'Thu' },
                          { code: 'fri', label: 'Fri' },
                          { code: 'sat', label: 'Sat' },
                          { code: 'sun', label: 'Sun' },
                        ].map(day => (
                          <Button
                            key={day.code}
                            type="button"
                            variant={watch('operating_weekdays')?.includes(day.code) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              const current = watch('operating_weekdays') || [];
                              if (current.includes(day.code)) {
                                setValue('operating_weekdays', current.filter(d => d !== day.code));
                              } else {
                                setValue('operating_weekdays', [...current, day.code]);
                              }
                            }}
                            className="flex-1"
                          >
                            {day.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h4 className="font-medium text-sm mb-3">Notes</h4>
                  <Textarea
                    id="description"
                    {...register('description')}
                    placeholder="Additional information about this site"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Node Type Selection (for non-sites) */}
          {watchedType !== 'site' && step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Select the type of node you want to create in your asset hierarchy.</p>
              
              <div className="grid grid-cols-2 gap-4">
                {(['site', 'zone', 'system', 'endpoint', 'shared_infrastructure', 'supplier', 'cloud'] as NodeType[]).map((type) => (
                  <div
                    key={type}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors hover:bg-muted ${
                      watchedType === type ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                    onClick={() => setValue('type', type)}
                  >
                    <div className="flex items-center gap-3">
                      <NodeTypeIcon type={type} />
                      <div>
                        <h4 className="font-medium capitalize">{type.replace('_', ' ')}</h4>
                        <p className="text-xs text-muted-foreground">
                          {type === 'site' && 'Physical location or facility'}
                          {type === 'zone' && 'Logical/physical boundary within a site'}
                          {type === 'system' && 'Functional grouping of endpoints'}
                          {type === 'endpoint' && 'Individual device or component'}
                          {type === 'supplier' && 'External service provider'}
                          {type === 'cloud' && 'Cloud service or platform'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {watchedType && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h5 className="font-medium mb-2">Hierarchy Position</h5>
                  <p className="text-sm text-muted-foreground">
                    {watchedType === 'zone' && 'Child of: Site → Contains systems and infrastructure'}
                    {watchedType === 'system' && 'Child of: Zone → Contains endpoints'}
                    {watchedType === 'endpoint' && 'Child of: System → Individual device'}
                    {watchedType === 'supplier' && 'Independent → Can manage systems/endpoints'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Basic Information (for non-sites) */}
          {watchedType !== 'site' && step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="label">Name *</Label>
                  <Input
                    id="label"
                    {...register('label')}
                    placeholder={`Enter ${watchedType} name`}
                  />
                  {errors.label && (
                    <p className="text-sm text-danger">{errors.label.message}</p>
                  )}
                </div>

                {/* Zone Type Selection for Zones */}
                {watchedType === 'zone' && (
                  <div>
                    <Label htmlFor="zoneType">Zone Type *</Label>
                    <Select
                      value={watchedZoneType}
                      onValueChange={(value) => setValue('zoneType', value as ZoneType)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select zone type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IT">IT Zone (L3.5, L4, L5)</SelectItem>
                        <SelectItem value="OT">OT Zone (L0, L1, L2)</SelectItem>
                        <SelectItem value="DMZ">DMZ Zone</SelectItem>
                        <SelectItem value="Cloud">Cloud & Services</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Parent Selection */}
                {watchedType !== 'supplier' && (
                  <div>
                    <Label htmlFor="parentId">{getParentLabel()} *</Label>
                    <Select onValueChange={(value) => setValue('parentId', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${getParentLabel().toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {getParentNodes().map((node) => (
                          <SelectItem key={node.id} value={node.id}>
                            {node.data.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Domain Selection - not for sites or suppliers */}
                {(watchedType === 'zone' || watchedType === 'system' || watchedType === 'endpoint') && (
                  <div>
                    <Label htmlFor="domain">Domain</Label>
                    <Select onValueChange={(value) => setValue('domain', value as Domain)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select domain" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IT">IT</SelectItem>
                        <SelectItem value="OT">OT</SelectItem>
                        <SelectItem value="Cloud">Cloud</SelectItem>
                        <SelectItem value="External">External</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Purdue Level Selection */}
                {(watchedType === 'zone' || watchedType === 'system' || watchedType === 'endpoint') && (
                  <div>
                    <Label htmlFor="purdue_level">Purdue Level</Label>
                    <Select onValueChange={(value) => setValue('purdue_level', value as Purdue)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Purdue level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="L0">L0 - Physical Process</SelectItem>
                        <SelectItem value="L1">L1 - Intelligent Devices</SelectItem>
                        <SelectItem value="L2">L2 - Local Control & Supervision</SelectItem>
                        <SelectItem value="L3.5">L3.5 - Demilitarized Zone (DMZ)</SelectItem>
                        <SelectItem value="L4">L4 - Business Logistics</SelectItem>
                        <SelectItem value="L5">L5 - Enterprise Network</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Site Selection for endpoints */}
                {(watchedType === 'endpoint' || watchedType === 'system') && sites.length > 0 && (
                  <div>
                    <Label htmlFor="siteId">Site</Label>
                    <Select onValueChange={(value) => setValue('siteId', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select site" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Step 3: Technical Details (for non-sites) */}
          {watchedType !== 'site' && step === 3 && (
            <div className="space-y-4">
              {/* Technical details for endpoints */}
              {watchedType === 'endpoint' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ip">IP Address</Label>
                    <Input
                      id="ip"
                      {...register('ip')}
                      placeholder="192.168.1.100"
                    />
                  </div>

                  <div>
                    <Label htmlFor="model">Model</Label>
                    <Input
                      id="model"
                      {...register('model')}
                      placeholder="Device model"
                    />
                  </div>

                  <div>
                    <Label htmlFor="vendor">Vendor</Label>
                    <Input
                      id="vendor"
                      {...register('vendor')}
                      placeholder="Manufacturer"
                    />
                  </div>

                  <div>
                    <Label htmlFor="serial">Serial Number</Label>
                    <Input
                      id="serial"
                      {...register('serial')}
                      placeholder="Serial number"
                    />
                  </div>

                  <div>
                    <Label htmlFor="fw_version">Firmware Version</Label>
                    <Input
                      id="fw_version"
                      {...register('fw_version')}
                      placeholder="1.2.3"
                    />
                  </div>

                  <div>
                    <Label htmlFor="criticality">Criticality</Label>
                    <Select onValueChange={(value) => setValue('criticality', value as 'High' | 'Medium' | 'Low')}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select criticality" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="lifecycle_state">Lifecycle State</Label>
                    <Select onValueChange={(value) => setValue('lifecycle_state', value as 'prod' | 'test' | 'dev' | 'retired')}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prod">Production</SelectItem>
                        <SelectItem value="test">Test</SelectItem>
                        <SelectItem value="dev">Development</SelectItem>
                        <SelectItem value="retired">Retired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Show Purdue level for OT assets */}
                  {watchedDomain === 'OT' && (
                    <div>
                      <Label htmlFor="purdue_level">Purdue Level</Label>
                      <Select onValueChange={(value) => setValue('purdue_level', value as Purdue)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="L0">L0 - Physical Process</SelectItem>
                          <SelectItem value="L1">L1 - Basic Control</SelectItem>
                          <SelectItem value="L2">L2 - Area Supervisory</SelectItem>
                          <SelectItem value="L3">L3 - Site Operations</SelectItem>
                          <SelectItem value="L3.5">L3.5 - Site Logistics</SelectItem>
                          <SelectItem value="L4">L4 - Business Logistics</SelectItem>
                          <SelectItem value="L5">L5 - Enterprise</SelectItem>
                          <SelectItem value="DMZ">DMZ</SelectItem>
                          <SelectItem value="Corp">Corporate</SelectItem>
                          <SelectItem value="Safety">Safety</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Supplier-specific fields */}
              {watchedType === 'supplier' && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Configure external supplier or service provider details.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Contact Information</Label>
                      <Input
                        {...register('ip')}
                        placeholder="Email or phone"
                      />
                    </div>
                    
                    <div>
                      <Label>Service Type</Label>
                      <Input
                        {...register('category')}
                        placeholder="OEM, ISP, SOC, etc."
                      />
                    </div>

                    <div className="col-span-2">
                      <Label>Managed Systems</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Systems/assets this supplier manages (can be configured later)
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {(watchedType === 'zone' || watchedType === 'system') && (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Additional technical details can be configured after creation through the inspector panel.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            <div>
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>

              {watchedType === 'site' ? (
                <Button type="submit">
                  Create Site
                </Button>
              ) : step < totalSteps ? (
                <Button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceedToNextStep()}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button type="submit">
                  {watchedType === 'zone' && 'Create Zone'}
                  {watchedType === 'system' && 'Create System'}
                  {watchedType === 'endpoint' && 'Create Endpoint'}
                  {watchedType === 'supplier' && 'Create Supplier'}
                </Button>
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}